import React, { useState, useEffect, useCallback, useRef } from 'react';
import { feedService } from '../../services/feed';
import { authService } from '../../services/auth';
import { voteService } from '../../services/vote';
import { masterEventService } from '../../services/masterEvent';
import Avatar from './Avatar';
import TeamTag from './TeamTag';

export default function ChatSidebar({ masterEventId, user, feed }) {
  const allUsers = authService.getAllUsers();
  const [chatMsg, setChatMsg] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [minimized, setMinimized] = useState(false);
  const [sending, setSending] = useState(false);
  const [chatError, setChatError] = useState('');
  const inputRef = useRef(null);

  const loadChat = useCallback(async () => {
    if (!masterEventId) {
      setChatMessages([]);
      return;
    }
    const messages = await feedService.getForMasterEvent(masterEventId, 5);
    setChatMessages(messages);
  }, [masterEventId]);

  useEffect(() => {
    loadChat();
  }, [loadChat, feed]);

  const memberNames = allUsers.map((u) => ({ id: u.id, name: u.displayName, username: u.username }));
  const filteredMentions = mentionQuery
    ? memberNames.filter((m) => m.name.toLowerCase().includes(mentionQuery.toLowerCase()) || m.username?.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 5)
    : memberNames.slice(0, 5);

  const handleInput = (e) => {
    const val = e.target.value;
    setChatMsg(val);
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    if (atMatch) { setShowMentions(true); setMentionQuery(atMatch[1]); }
    else { setShowMentions(false); setMentionQuery(''); }
  };

  const insertMention = (name) => {
    const cursorPos = inputRef.current?.selectionStart || chatMsg.length;
    const textBefore = chatMsg.slice(0, cursorPos);
    const textAfter = chatMsg.slice(cursorPos);
    setChatMsg(textBefore.replace(/@\w*$/, `@${name} `) + textAfter);
    setShowMentions(false); setMentionQuery('');
    inputRef.current?.focus();
  };

  const handleSend = async () => {
    if (!chatMsg.trim() || !user || sending) return;
    setSending(true);
    setChatError('');

    // Check for /vote command
    const voteParsed = voteService.parseVoteCommand(chatMsg.trim());
    if (voteParsed) {
      try {
      // Create a vote activity and the poll
      const activity = await masterEventService.createActivity({
        masterEventId,
        type: 'vote',
        name: voteParsed.question.length > 40 ? voteParsed.question.slice(0, 40) + '...' : voteParsed.question,
        description: voteParsed.question,
      });
      voteService.create({
        activityId: activity.id,
        masterEventId,
        question: voteParsed.question,
        options: voteParsed.options,
        createdBy: user.id,
      });
      await feedService.post({ masterEventId, type: 'vote_created', userId: user.id, content: `🗳️ Created a poll: "${voteParsed.question}" — Go to Activities to vote!` });
      setChatMsg(''); setShowMentions(false);
      await loadChat();
      } catch (err) {
        setChatError(err.message || 'Could not create vote');
      } finally {
        setSending(false);
      }
      return;
    }

    try {
      await feedService.post({ masterEventId, type: 'chat', userId: user.id, content: chatMsg.trim() });
      setChatMsg(''); setShowMentions(false);
      await loadChat();
    } catch (err) {
      setChatError(err.message || 'Could not send message');
    } finally {
      setSending(false);
    }
  };

  const renderContent = (text) => {
    return text.split(/(@\w+)/g).map((part, i) => {
      if (part.startsWith('@')) return <span key={i} style={{ color: '#60a5fa', fontWeight: 600 }}>{part}</span>;
      return part;
    });
  };

  if (minimized) {
    return (
      <div className="hub-chat-sidebar hub-chat-sidebar--minimized" onClick={() => setMinimized(false)}>
        <span style={{ fontSize: 16 }}>💬</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>Chat</span>
        {chatMessages.length > 0 && <span className="hub-chat-unread-dot" />}
      </div>
    );
  }

  return (
    <div className="hub-chat-sidebar">
      <div className="hub-chat-header">
        <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>Quick Chat</span>
        <button className="hub-chat-minimize" onClick={() => setMinimized(true)} title="Minimize">─</button>
      </div>
      {chatError && (
        <div style={{ color: '#f87171', fontSize: 12, marginBottom: 8 }}>{chatError}</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10, maxHeight: 200, overflowY: 'auto' }}>
        {chatMessages.length === 0 ? (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>No messages yet</div>
        ) : (
          [...chatMessages].reverse().map((msg) => {
            const msgUser = allUsers.find((u) => u.id === msg.userId);
            return (
              <div key={msg.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 8px', background: 'rgba(255,255,255,0.04)', borderRadius: 8 }}>
                <Avatar user={msgUser} size={14} style={{ flexShrink: 0 }} />
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>
                  <strong style={{ color: '#fff' }}>{msgUser?.displayName || 'Unknown'}</strong><TeamTag user={msgUser} />{' '}
                  {renderContent(msg.content)}
                </div>
              </div>
            );
          })
        )}
      </div>
      <div style={{ position: 'relative' }}>
        {showMentions && (
          <div className="hub-mention-dropdown">
            {filteredMentions.map((m) => (
              <button key={m.id} className="hub-mention-option" onClick={() => insertMention(m.name)}>
                <span>{m.name}</span>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>@{m.username || m.name.toLowerCase()}</span>
              </button>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <input ref={inputRef} type="text" value={chatMsg} onChange={handleInput}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !showMentions) { e.preventDefault(); handleSend(); } }}
            placeholder="Type a message... @mention to tag"
            style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 13, outline: 'none' }} />
          <button onClick={handleSend} disabled={!chatMsg.trim() || sending}
            style={{ background: '#22c55e', border: 'none', borderRadius: 8, padding: '8px 14px', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: chatMsg.trim() && !sending ? 1 : 0.5 }}>
            {sending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
